import { useTranslation } from 'react-i18next'
import { MailX, MailWarning } from 'lucide-react'
import type { BounceCategory, SegmentEnrollmentPreview } from '@/types/api'

interface EnrollScreeningGroupsProps {
  preview: SegmentEnrollmentPreview
}

const BOX = 'rounded-md border p-3 space-y-2'
const LIST = 'space-y-1 max-h-32 overflow-y-auto'

/**
 * The checks of a segment enrollment (#386): domains that receive no email,
 * bounces known to Multicadence, hard bounces recorded by HubSpot. A contact
 * is in one group at most.
 */
export function EnrollScreeningGroups({ preview }: EnrollScreeningGroupsProps) {
  const { t } = useTranslation(['sequences', 'contacts'])
  const invalidDomains = preview.invalid_domains ?? []
  const knownBounces = preview.known_bounces ?? []
  const hardBounces = preview.hubspot_hard_bounces ?? []

  const bounceReason = (code: string | null, category: BounceCategory | null) => {
    const reason = t(`contacts:emails.bounceReason.categories.${category ?? 'sconosciuto'}`)
    return code ? `${reason} (${code})` : reason
  }

  const hubspotReason = (reason: string) =>
    t(`enrollDialog.preview.screening.hubspotReasons.${reason}`, { defaultValue: reason })

  return (
    <>
      {invalidDomains.length > 0 && (
        <div
          data-testid="screening-invalid-domains"
          className={`${BOX} border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950`}
        >
          <div className="flex items-center gap-2 text-sm font-medium text-red-700 dark:text-red-400">
            <MailX className="h-4 w-4 shrink-0" />
            {t('enrollDialog.preview.screening.invalidDomains', { count: invalidDomains.length })}
          </div>
          <ul className={LIST}>
            {invalidDomains.map((c) => (
              <li key={c.contact_id} className="text-sm text-muted-foreground">
                {c.contact_name && c.contact_name !== c.contact_email
                  ? `${c.contact_name} — ${c.contact_email}`
                  : c.contact_email}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(knownBounces.length > 0 || hardBounces.length > 0) && (
        <div className={`${BOX} border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950`}>
          {knownBounces.length > 0 && (
            <div data-testid="screening-known-bounces" className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                <MailWarning className="h-4 w-4 shrink-0" />
                {t('enrollDialog.preview.screening.knownBounces', { count: knownBounces.length })}
              </div>
              <ul className={LIST}>
                {knownBounces.map((c) => (
                  <li key={c.contact_id} className="text-sm text-muted-foreground">
                    {c.contact_name || c.contact_email}{' '}
                    {t('enrollDialog.preview.screening.knownBounceIn', { sequence: c.sequence_name })}
                    {' — '}
                    {bounceReason(c.bounce_code, c.bounce_category)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hardBounces.length > 0 && (
            <div data-testid="screening-hubspot-hard-bounces" className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                <MailWarning className="h-4 w-4 shrink-0" />
                {t('enrollDialog.preview.screening.hubspotHardBounces', { count: hardBounces.length })}
              </div>
              <ul className={LIST}>
                {hardBounces.map((c) => (
                  <li key={c.contact_id} className="text-sm text-muted-foreground">
                    {c.contact_name || c.contact_email} — {hubspotReason(c.reason)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-amber-700 dark:text-amber-400">
            {t('enrollDialog.preview.screening.excludedUnlessIncluded')}
          </p>
        </div>
      )}
    </>
  )
}
